/**
 * SOLOMON — LEADS LIST
 * Pipeline view: state tabs + search + clickable rows.
 *
 * Fetches GET /api/leads with optional ?state= filter. Tabs that group
 * multiple states (e.g. "Responded" = warm + unsure) fetch by 'all' and
 * filter client-side — server endpoint accepts only single-state filters.
 *
 * The 'stale' tab (mode='stale') is a different endpoint: GET /api/leads/stale,
 * which returns leads ingested ≥ 72 hours ago AND never recently contacted.
 * When this tab is active, per-row checkboxes + an "Archive selected" button
 * surface so the operator can bulk-archive stale leads via POST /api/leads/bulk-archive.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  STATE_TABS,
  STATE_LABELS,
  STATE_CLASS,
  formatRelativeTime,
} from './leadsConstants';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const STALE_HOURS = 72;

// Filter chips for the strip above the lead table. Order = display order.
// Keys match leadStateService.FILTERABLE_SIGNALS (server-side allow list)
// and the leads.is_${key} columns. Labels are operator-facing.
const FILTERABLE_SIGNALS = [
  { key: 'preforeclosure',  label: 'Pre-foreclosure' },
  { key: 'tax_default',     label: 'Tax default' },
  { key: 'inherited',       label: 'Inherited' },
  { key: 'vacant',          label: 'Vacant' },
  { key: 'absentee',        label: 'Absentee' },
  { key: 'tired_landlord',  label: 'Tired landlord' },
  { key: 'failed_listing',  label: 'Failed listing' },
  { key: 'senior_owner',    label: 'Senior owner' },
  { key: 'arv_conflict',    label: 'ARV conflict' },  // tri-engine committee disagrees >5%
];

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export default function LeadsList({ onSelect, refreshKey = 0 }) {
  const { authFetch } = useAuth();

  const [activeTab, setActiveTab] = useState('non_contacted');
  const [search, setSearch]       = useState('');
  const [sort, setSort]           = useState('motivation');         // 'motivation' | 'date'
  const [activeSignals, setActiveSignals] = useState(() => new Set()); // signal-filter chips
  const [publicRecordsOnly, setPublicRecordsOnly] = useState(false);   // client-side lead_origin filter
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  // Aggregate counts so every tab can show its number, not just the active one.
  // Shape: { all: N, by_state: { new: N, contacted: N, ... } }
  const [stateCounts, setStateCounts] = useState({ all: 0, by_state: {} });
  const [staleCount, setStaleCount]   = useState(0);

  // Bulk-select state. Active only on the stale tab; cleared on tab switch.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [archiving, setArchiving]     = useState(false);

  // Skip-trace bulk state
  // stStatus: idle | checking | confirm | running | done
  const [stStatus,   setStStatus]   = useState('idle');
  const [stEstimate, setStEstimate] = useState(null);   // { eligibleCount, estCents }
  const [stResult,   setStResult]   = useState(null);   // { matched, traced } | { zeroEligible: true }
  const [stError,    setStError]    = useState(null);

  // Public-records pull (Hamilton sheriff sale) — free ground-truth ingest.
  const [pulling, setPulling] = useState(false);
  const [pullMsg, setPullMsg] = useState(null);

  const isStaleTab = activeTab === 'stale';

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tabDef = STATE_TABS.find((t) => t.id === activeTab);
      let url;
      if (tabDef?.mode === 'stale') {
        // Stale endpoint has its own sort + scope; signal filters / sort
        // dropdown don't apply on this tab.
        url = `${API_BASE}/api/leads/stale?hours=${STALE_HOURS}&limit=500`;
      } else {
        const params = new URLSearchParams();
        params.set('limit', '500');
        if (sort !== 'motivation') params.set('sort', sort);   // omit default for clean URL
        if (activeSignals.size > 0) params.set('signals', Array.from(activeSignals).join(','));
        if (tabDef?.states?.length === 1) params.set('state', tabDef.states[0]);
        url = `${API_BASE}/api/leads?${params.toString()}`;
      }
      const res = await authFetch(url);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      setLeads(data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [authFetch, activeTab, sort, activeSignals]);

  // Pull today's Hamilton County sheriff-sale (foreclosure) leads — free public
  // records, tagged public_records/verified with the on-site distress factor.
  // Never spends: leads land phoneless; skip-trace (gated) makes them callable.
  const pullHamilton = useCallback(async () => {
    setPulling(true);
    setPullMsg(null);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/public-records/hamilton`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      const d = data.data || {};
      const added = d.inserted?.length || 0;
      const dup = d.skipped?.length || 0;
      setPullMsg(added > 0
        ? `+${added} new foreclosure lead${added === 1 ? '' : 's'}${dup ? ` · ${dup} already in pipeline` : ''} — skip-trace to call`
        : `No new leads — all ${dup} already in your pipeline`);
      await fetchLeads();
    } catch (err) {
      setPullMsg(`Pull failed: ${err.message}`);
    } finally {
      setPulling(false);
    }
  }, [authFetch, fetchLeads]);

  const fetchStateCounts = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/leads/state-counts`);
      const data = await res.json();
      if (!res.ok || !data.success) return;
      setStateCounts(data.data || { all: 0, by_state: {} });
    } catch {
      // Silent — counts are decorative, not blocking
    }
  }, [authFetch]);

  // Separate stale-count probe so the "Stale (3d+)" tab shows its number
  // even when we're on a different tab. Cheap (LIMIT 500), 60s cache by
  // virtue of being called on refreshKey changes only.
  const fetchStaleCount = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/leads/stale?hours=${STALE_HOURS}&limit=500`);
      const data = await res.json();
      if (res.ok && data.success) setStaleCount((data.data || []).length);
    } catch {
      // Silent — count is decorative
    }
  }, [authFetch]);

  useEffect(() => { fetchLeads(); },       [fetchLeads,       refreshKey]);
  useEffect(() => { fetchStateCounts(); }, [fetchStateCounts, refreshKey]);
  useEffect(() => { fetchStaleCount(); },  [fetchStaleCount,  refreshKey]);

  // Switching tabs clears the bulk-select set + signal filters (the
  // filter strip lives on non-stale tabs; carrying selection across is
  // surprising).
  useEffect(() => {
    setSelectedIds(new Set());
    setActiveSignals(new Set());
  }, [activeTab]);

  // Toggle a signal in/out of the active filter set.
  const toggleSignal = useCallback((key) => {
    setActiveSignals((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Count of leads in the currently-loaded set that carry each signal —
  // shown as the small number next to each filter chip. Reflects the
  // already-filtered view (so as filters narrow, counts narrow), which
  // is the simplest correct semantic for v1.
  const signalCounts = useMemo(() => {
    const counts = {};
    for (const sig of FILTERABLE_SIGNALS) counts[sig.key] = 0;
    for (const lead of leads) {
      for (const sig of FILTERABLE_SIGNALS) {
        if (lead[`is_${sig.key}`] === true) counts[sig.key]++;
      }
    }
    return counts;
  }, [leads]);

  const filtered = useMemo(() => {
    const tabDef = STATE_TABS.find((t) => t.id === activeTab);
    let rows = leads;

    // Client-side state filter for grouped tabs (server already filtered single-state tabs).
    // Stale tab skips this — its endpoint already returns the filtered set.
    if (tabDef?.mode !== 'stale' && tabDef && tabDef.states && tabDef.states.length > 1) {
      const allowed = new Set(tabDef.states);
      rows = rows.filter((l) => allowed.has(l.state));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((l) => {
        const hay = [
          l.property_address,
          l.property_city,
          l.owner_full_name,
          l.owner_first_name,
          l.owner_last_name,
          l.phone_primary,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }

    // Client-side origin filter — ground-truth public-records leads only.
    if (publicRecordsOnly) {
      rows = rows.filter((l) => l.lead_origin === 'public_records');
    }
    return rows;
  }, [leads, activeTab, search, publicRecordsOnly]);

  const counts = useMemo(() => {
    const byState = stateCounts.by_state || {};
    const out = {};
    for (const tab of STATE_TABS) {
      if (tab.mode === 'stale') {
        out[tab.id] = staleCount;
      } else if (tab.states && tab.states.length > 0) {
        out[tab.id] = tab.states.reduce((sum, s) => sum + (byState[s] || 0), 0);
      } else {
        out[tab.id] = 0;
      }
    }
    return out;
  }, [stateCounts, staleCount]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      // If everything visible is already selected, clear; else select all.
      const allVisible = filtered.map((l) => l.id);
      const allSelected = allVisible.length > 0 && allVisible.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(allVisible);
    });
  }, [filtered]);

  const bulkArchive = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const confirmMsg = `Archive ${ids.length} lead${ids.length === 1 ? '' : 's'}?\n\nSoft delete — rows stay for audit but are hidden from queues, lists, and briefings.`;
    if (!window.confirm(confirmMsg)) return;

    setArchiving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/leads/bulk-archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Bulk archive failed');
      setSelectedIds(new Set());
      // Reload list + counts so the archived rows disappear and the
      // Stale-tab count drops.
      await Promise.all([fetchLeads(), fetchStateCounts(), fetchStaleCount()]);
    } catch (err) {
      window.alert(`Bulk archive failed: ${err.message}`);
    } finally {
      setArchiving(false);
    }
  }, [authFetch, selectedIds, fetchLeads, fetchStateCounts, fetchStaleCount]);

  // --- Skip-trace callbacks ---
  const skipTraceCheck = useCallback(async () => {
    setStStatus('checking');
    setStError(null);
    setStResult(null);
    try {
      const body = { dryRun: true };
      if (selectedIds.size > 0) body.leadIds = Array.from(selectedIds);
      const res = await authFetch(`${API_BASE}/api/leads/skip-trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Skip-trace check failed');
      const { eligibleCount, estCents } = data.data;
      if (eligibleCount === 0) {
        setStResult({ zeroEligible: true });
        setStStatus('done');
      } else {
        setStEstimate({ eligibleCount, estCents });
        setStStatus('confirm');
      }
    } catch (err) {
      setStError(err.message || 'Skip-trace check failed');
      setStStatus('idle');
    }
  }, [authFetch, selectedIds]);

  const skipTraceRun = useCallback(async () => {
    setStStatus('running');
    setStError(null);
    try {
      const body = {};
      if (selectedIds.size > 0) body.leadIds = Array.from(selectedIds);
      const res = await authFetch(`${API_BASE}/api/leads/skip-trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Skip-trace failed');
      const { matched, traced } = data.data;
      setStResult({ matched, traced });
      setStStatus('done');
      await Promise.all([fetchLeads(), fetchStateCounts(), fetchStaleCount()]);
    } catch (err) {
      setStError(err.message || 'Skip-trace failed');
      setStStatus('confirm');
    }
  }, [authFetch, selectedIds, fetchLeads, fetchStateCounts, fetchStaleCount]);

  const skipTraceReset = useCallback(() => {
    setStStatus('idle');
    setStEstimate(null);
    setStResult(null);
    setStError(null);
  }, []);

  const allVisibleSelected = filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id));
  const someSelected       = selectedIds.size > 0;

  return (
    <div className="leads-list">
      <div className="leads-list-controls">
        <div className="leads-list-search">
          <SearchIcon />
          <input
            type="text"
            className="leads-list-search-input"
            placeholder="Search address, owner, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        {!isStaleTab && (
          <select
            className="leads-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort leads"
          >
            <option value="motivation">Most motivated</option>
            <option value="date">Newest</option>
          </select>
        )}
        {isStaleTab && someSelected && (
          <button
            className="leads-bulk-archive-btn"
            onClick={bulkArchive}
            disabled={archiving}
          >
            {archiving ? 'Archiving…' : `Archive selected (${selectedIds.size})`}
          </button>
        )}
        <button
          className="btn-primary leads-skiptrace-trigger"
          onClick={skipTraceCheck}
          disabled={stStatus === 'checking' || stStatus === 'running'}
        >
          {stStatus === 'checking' ? 'Checking…' : selectedIds.size > 0 ? 'Skip-trace selected' : 'Skip-trace'}
        </button>
        <button
          className="leads-pull-btn"
          onClick={pullHamilton}
          disabled={pulling}
          title="Pull today's Hamilton County sheriff-sale (foreclosure) leads — free public records, tagged verified. Skip-trace separately to make them callable."
        >
          {pulling ? 'Pulling…' : '+ Hamilton foreclosures'}
        </button>
        {pullMsg && <span className="leads-pull-msg">{pullMsg}</span>}
      </div>

      {/* Skip-trace inline confirm / status strip */}
      {stStatus === 'confirm' && stEstimate && (
        <div className="leads-skiptrace-bar">
          <span className="leads-skiptrace-bar-msg">
            Skip-trace {stEstimate.eligibleCount} lead{stEstimate.eligibleCount === 1 ? '' : 's'} for ~${(stEstimate.estCents / 100).toFixed(2)}?{selectedIds.size > 0 ? ' (selected — no phone yet)' : ' (leads with no phone yet)'}
          </span>
          <div className="leads-skiptrace-bar-actions">
            <button className="btn-primary leads-skiptrace-trigger" onClick={skipTraceRun}>Confirm</button>
            <button className="leads-filter-clear" onClick={skipTraceReset}>Cancel</button>
          </div>
        </div>
      )}
      {stStatus === 'running' && (
        <div className="leads-skiptrace-bar">
          <div className="leads-import-pulse" />
          <span>Skip-tracing…</span>
        </div>
      )}
      {stStatus === 'done' && stResult && (
        <div className="leads-skiptrace-bar">
          <span className="leads-skiptrace-bar-msg">
            {stResult.zeroEligible
              ? 'All leads already have phones.'
              : `${stResult.matched} of ${stResult.traced} leads got phones.`}
          </span>
          <button className="leads-filter-clear" onClick={skipTraceReset}>Dismiss</button>
        </div>
      )}
      {stError && (
        <div className="leads-import-error">{stError}</div>
      )}

      <div className="leads-list-tabs" role="tablist">
        {STATE_TABS.map((tab) => {
          const count = counts[tab.id];
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              className={`leads-list-tab ${isActive ? 'is-active' : ''} ${tab.mode === 'stale' ? 'leads-list-tab--stale' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
              <span className="leads-list-tab-count">{count ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Signal filter chip strip — non-stale tabs only. Chips with zero
          count are hidden unless currently active (so clearing keeps the
          UI from going empty). */}
      {!isStaleTab && (
        <div className="leads-filter-strip">
          {/* Origin filter — ground-truth public-records leads (foreclosure /
              tax-sale / code cases). Client-side on lead_origin; visually
              distinct (gold) so it reads as a quality facet, not a signal. */}
          {(() => {
            const prCount = leads.filter((l) => l.lead_origin === 'public_records').length;
            if (prCount === 0 && !publicRecordsOnly) return null;
            return (
              <button
                className={`leads-filter-chip leads-filter-chip--origin ${publicRecordsOnly ? 'is-active' : ''}`}
                onClick={() => setPublicRecordsOnly((v) => !v)}
                aria-pressed={publicRecordsOnly}
                title="Show only ground-truth leads sourced from public records (foreclosure / tax-sale / code cases)"
              >
                Public records
                <span className="leads-filter-chip-count">{prCount}</span>
              </button>
            );
          })()}
          {FILTERABLE_SIGNALS.map(({ key, label }) => {
            const count = signalCounts[key] || 0;
            const active = activeSignals.has(key);
            if (count === 0 && !active) return null;
            return (
              <button
                key={key}
                className={`leads-filter-chip ${active ? 'is-active' : ''}`}
                onClick={() => toggleSignal(key)}
                aria-pressed={active}
              >
                {label}
                <span className="leads-filter-chip-count">{count}</span>
              </button>
            );
          })}
          {activeSignals.size > 0 && (
            <button
              className="leads-filter-clear"
              onClick={() => setActiveSignals(new Set())}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {isStaleTab && filtered.length > 0 && (
        <div className="leads-stale-hint">
          {filtered.length} lead{filtered.length === 1 ? '' : 's'} ingested 3+ days ago with no recent contact.
          Select rows to archive (soft delete — row stays for audit).
        </div>
      )}

      {error && <div className="leads-import-error">{error}</div>}

      {loading && (
        <div className="leads-list-loading">
          <div className="leads-import-pulse" />
          <div>Loading leads…</div>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="leads-list-empty">
          {leads.length === 0
            ? (isStaleTab
                ? 'No stale leads. Everything was either ingested in the last 3 days or contacted recently.'
                : 'No leads yet. Use Tools → Import Leads to bring in a CSV.')
            : (search.trim()
                ? `No matches for "${search}".`
                : `No leads in this category.`)}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="leads-list-table-wrap">
          <table className="leads-list-table sol-table">
            <thead>
              <tr>
                {/* Checkboxes on every tab (2026-07-01): selection drives both
                    "Archive selected" (stale tab) and "Skip-trace selected"
                    (any tab) — they were stale-tab-only, which made the
                    selected-leads skip-trace flow unreachable in practice. */}
                <th className="leads-list-checkbox-col">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAll}
                    aria-label="Select all visible"
                  />
                </th>
                <th>State</th>
                <th>Owner</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Imported</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead) => (
                <tr
                  key={lead.id}
                  className={`leads-list-row ${selectedIds.has(lead.id) ? 'is-selected' : ''}`}
                  onClick={(e) => {
                    // Don't bubble the row click when the click originated on the checkbox cell.
                    if (e.target.tagName === 'INPUT' || e.target.closest('.leads-list-checkbox-col')) return;
                    onSelect(lead.id);
                  }}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(lead.id);
                    }
                  }}
                >
                  <td className="leads-list-checkbox-col" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={() => toggleOne(lead.id)}
                      aria-label={`Select ${lead.owner_full_name || lead.property_address}`}
                    />
                  </td>
                  <td>
                    <span className={`leads-state-pill ${STATE_CLASS[lead.state] || ''}`}>
                      {STATE_LABELS[lead.state] || lead.state}
                    </span>
                  </td>
                  <td>{lead.owner_full_name || '—'}</td>
                  <td className="leads-list-address">
                    <div className="leads-list-address-line">
                      {lead.property_address}
                      {lead.property_city && (
                        <span className="leads-list-city">
                          , {lead.property_city} {lead.property_state || ''}
                        </span>
                      )}
                    </div>
                    {lead.lead_origin === 'public_records' && (
                      <span
                        className="leads-origin-badge"
                        title={lead.source_metadata?.distress_factor
                          ? `Verified distress: ${lead.source_metadata.distress_factor}`
                          : 'Sourced from public records (ground-truth)'}
                      >
                        {lead.source_metadata?.distress_factor
                          || `Public records${lead.signal_provenance === 'verified' ? ' · verified' : ''}`}
                      </span>
                    )}
                    {Array.isArray(lead.chips) && lead.chips.length > 0 && (
                      <div className="leads-list-chips">
                        {lead.chips.map((chip, i) => (
                          <span
                            key={`${chip.key}-${i}`}
                            className={`leads-chip leads-chip--${chip.severity || 'medium'}`}
                            title={chip.weight != null ? `weight ${chip.weight}` : undefined}
                          >
                            {chip.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="leads-list-mono">{lead.phone_primary || '—'}</td>
                  <td className="leads-list-time">{formatRelativeTime(lead.created_at || lead.imported_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
